// Formats a rule as a human-readable sentence like:
// "If Task Classification is Red AND Trade is Plumbing"

export function formatRuleSummary(rule, conditionFields = []) {
    const conditions = rule?.conditions || []
    if (!conditions.length) return "No conditions"
    const parts = conditions.map(formatConditionWithFields).filter(Boolean)
    if (!parts.length) return "No conditions"
    const separator = rule?.combinator === "or" ? " OR " : " AND "
    return parts.join(separator)

    function formatConditionWithFields(c) {
        return formatCondition(c, conditionFields)
    }
}

export function formatActionLabel(action) {
    const type = action?.type || "include"
    if (type === "include") return "Include"
    if (type === "exclude") return "Exclude"
    if (type === "modifier") return formatCostModifier(action)
    if (type === "timeModifier") return formatTimeModifier(action)
    if (type === "frequencyModifier") return formatFrequencyModifier(action)
    return type.charAt(0).toUpperCase() + type.slice(1)
}

function formatCostModifier(action) {
    if (action.costKind === "fixed" && action.fixedAmount != null) {
        const amount = Number(action.fixedAmount) || 0
        const sign = amount >= 0 ? "+" : ""
        return `Cost modifier ${sign}${amount} fixed`
    }
    if (action.multiplier != null) return `Cost modifier x${action.multiplier}`
    return "Cost modifier"
}

function formatTimeModifier(action) {
    if (action.timeKind === "percentage") {
        const pct = Math.round((Number(action.timeValue) || 0) * 100)
        const sign = pct >= 0 ? "+" : ""
        return `Time modifier ${sign}${pct}%`
    }
    const minutes = Number(action.timeValue) || 0
    const sign = minutes >= 0 ? "+" : ""
    return `Time modifier ${sign}${minutes} min`
}

function formatFrequencyModifier(action) {
    const multiplier = Number(action.frequencyMultiplier) || 1
    return `Frequency modifier ${multiplier}x`
}

function formatCondition(condition, conditionFields) {
    const fieldDef = conditionFields.find(matchesField)
    const fieldLabel = fieldDef?.label || condition.field || ""
    const operator = formatOperator(condition.operator, condition.value)
    const valueText = formatValue(condition.value)
    return [fieldLabel, operator, valueText].filter(Boolean).join(" ")

    function matchesField(f) {
        return f.value === condition.field
    }
}

function formatOperator(operator, value) {
    if (!operator) return ""
    // Simplify "is any of" / "is none of" when there's a single value
    if (operator === "is any of" && isSingleValue(value)) return "is"
    if (operator === "is none of" && isSingleValue(value)) return "is not"
    return operator
}

function isSingleValue(value) {
    if (Array.isArray(value)) return value.length === 1
    if (value && typeof value === "object") return false
    return value != null && value !== ""
}

function formatValue(value) {
    if (value == null || value === "") return ""
    if (Array.isArray(value)) {
        return value.map(formatSingleValue).filter(Boolean).join(", ")
    }
    if (typeof value === "object") {
        if (value.period && value.count != null) {
            return `${value.count} per ${value.period}`
        }
        return Object.values(value).filter(Boolean).join(" ")
    }
    return formatSingleValue(value)
}

function formatSingleValue(value) {
    if (value == null) return ""
    if (typeof value === "object" && value.id) return String(value.name || value.id)
    const str = String(value)
    if (!str.length) return ""
    // Capitalize first letter for display (e.g. "red" -> "Red")
    return str.charAt(0).toUpperCase() + str.slice(1)
}
